import { useEffect, useId, useRef, useState } from 'react';
import { ArrowRight, ChevronDown, X } from 'lucide-react';
import './canvas-connections-view.css';

// Reuse the environment's configured Bunny origin; provider assets are never bundled.
const logoBase = `${new URL(import.meta.env?.VITE_TOUR_MEDIA_BASE_URL || 'https://adsmanager-staging.b-cdn.net/onboarding/refreshed-20260930-v1').origin}/connections/provider-logos-20261023-v1`;
const assistantLogos = [
  { name: 'ChatGPT', file: 'chatgpt.png', className: 'chatgpt' },
  { name: 'Anthropic', file: 'anthropic.svg', className: 'anthropic' },
  { name: 'Grok', file: 'grok.svg', className: 'grok' },
] as const;

type ConnectionGuide = 'ai' | 'shopify';

const guides = {
  ai: {
    label: 'AI assistants',
    title: 'How AI connections will work',
    description: 'A guided connection will let you choose which Admoji context your assistant can use.',
    steps: [
      { title: 'Sign in to Admoji', description: 'Confirm your Admoji account when starting a connection from a supported assistant.' },
      { title: 'Review workspace access', description: 'Choose the workspace and review the permissions before sharing brand or product context.' },
      { title: 'Continue in your assistant', description: 'Use the approved context in your conversation. Available tools and permissions will be shown during setup.' },
    ],
  },
  shopify: {
    label: 'Shopify',
    title: 'How the Shopify connection will work',
    description: 'The planned app will bring product selection and creative briefs into your Shopify workflow.',
    steps: [
      { title: 'Install the Admoji app', description: 'An installation option will be provided when the app is available for your store. There is no install link yet.' },
      { title: 'Link your Admoji workspace', description: 'Sign in to Admoji, choose the workspace for your store, and review the requested permissions.' },
      { title: 'Choose a product', description: 'Select a Shopify product, confirm its brand context, and prepare a creative brief. Generation and saving creatives will require the completed integration.' },
    ],
  },
} as const;

/** Informational only: no account credentials, OAuth initiation, or remote calls. */
export function CanvasConnectionsView() {
  const [activeGuide, setActiveGuide] = useState<ConnectionGuide | null>(null);
  const guideId = useId();
  const guideHeading = useRef<HTMLHeadingElement>(null);
  const aiButton = useRef<HTMLButtonElement>(null);
  const shopifyButton = useRef<HTMLButtonElement>(null);
  const guide = activeGuide ? guides[activeGuide] : null;

  useEffect(() => {
    if (activeGuide) {
      guideHeading.current?.focus({ preventScroll: true });
      guideHeading.current?.closest('section')?.scrollIntoView({ block: 'start', behavior: 'instant' });
    }
  }, [activeGuide]);

  function closeGuide() {
    const trigger = activeGuide === 'ai' ? aiButton : shopifyButton;
    setActiveGuide(null);
    trigger.current?.focus();
  }

  return (
    <div className="admoji-connections" data-testid="canvas-connections-view">
      <header className="admoji-connections-header">
        <h1 className="workspace-page-title">Connections</h1>
        <p className="workspace-page-description">Explore new ways to use your Admoji workspace.</p>
      </header>
      <div className="admoji-connections-scroll">
        <div className="admoji-connections-content">
          <div className="admoji-connections-intro">
            <div>
              <p className="admoji-connections-eyebrow">YOUR WORKFLOW, IN MORE PLACES</p>
              <h2>Bring your brand context with you.</h2>
              <p>See what’s planned for AI assistants and Shopify, and how setup will work.</p>
            </div>
          </div>

          <div className="admoji-connections-grid">
            <article className="admoji-connections-card" aria-labelledby={`${guideId}-ai-title`}>
              <div className="admoji-connections-card-top">
                <div className="admoji-connections-providers" aria-label="AI assistant brands">
                  {assistantLogos.map(logo => <div className="admoji-connections-provider" key={logo.name}>
                    <span className="admoji-connections-provider-mark"><img src={`${logoBase}/${logo.file}`} alt="" className={`admoji-connections-logo-${logo.className}`} width="40" height="40" /></span>
                    <span>{logo.name}</span>
                  </div>)}
                </div>
                <span className="admoji-connections-status">In development</span>
              </div>
              <h3 id={`${guideId}-ai-title`}>AI assistants</h3>
              <p>Bring your brand and product context into a conversation with your AI assistant.</p>
              <div className="admoji-connections-card-detail"><span>Planned workflow</span><strong>Your context, inside your chat</strong></div>
              <div className="admoji-connections-card-actions">
                <button type="button" className="admoji-connections-button" ref={aiButton} aria-expanded={activeGuide === 'ai'} aria-controls={guideId} onClick={() => setActiveGuide('ai')}>
                  Explore AI connections <ArrowRight size={15} aria-hidden="true" />
                </button>
                <span>Connection setup is not available yet.</span>
              </div>
            </article>

            <article className="admoji-connections-card" aria-labelledby={`${guideId}-shopify-title`}>
              <div className="admoji-connections-card-top">
                <a className="admoji-connections-shopify-logo" href="https://www.shopify.com/" target="_blank" rel="noopener noreferrer" aria-label="Shopify website (opens in a new tab)"><img src={`${logoBase}/shopify.svg`} alt="Shopify" width="128" height="37" /></a>
                <span className="admoji-connections-status">In development</span>
              </div>
              <h3 id={`${guideId}-shopify-title`}>Shopify</h3>
              <p>Start with a product from your store and shape its next creative brief with Admoji.</p>
              <div className="admoji-connections-card-detail"><span>Planned workflow</span><strong>From your catalog to your creative</strong></div>
              <div className="admoji-connections-card-actions">
                <button type="button" className="admoji-connections-button" ref={shopifyButton} aria-expanded={activeGuide === 'shopify'} aria-controls={guideId} onClick={() => setActiveGuide('shopify')}>
                  Explore Shopify connection <ArrowRight size={15} aria-hidden="true" />
                </button>
                <span>App installation is not available yet.</span>
              </div>
            </article>
          </div>

          <section id={guideId} className="admoji-connections-guide" aria-labelledby={`${guideId}-heading`} hidden={!guide}>
            {guide && <>
              <div className="admoji-connections-guide-heading">
                <div><p className="admoji-connections-eyebrow">{guide.label} · Setup guide</p><h2 id={`${guideId}-heading`} ref={guideHeading} tabIndex={-1}>{guide.title}</h2></div>
                <button type="button" className="admoji-connections-close" onClick={closeGuide}><X size={15} aria-hidden="true" /><span>Close guide</span></button>
              </div>
              <p className="admoji-connections-guide-description">{guide.description}</p>
              <ol className="admoji-connections-guide-steps">{guide.steps.map((step, index) => <li key={step.title}><span className="admoji-connections-step-number" aria-hidden="true">{index + 1}</span><div><h3>{step.title}</h3><p>{step.description}</p></div></li>)}</ol>
              <p className="admoji-connections-guide-note">This is a preview of the planned setup. Opening this guide does not connect an account or grant access.</p>
              {activeGuide === 'ai' && <details className="admoji-connections-technical"><summary>Technical details <ChevronDown size={14} aria-hidden="true" /></summary><dl><div><dt>Remote MCP endpoint</dt><dd>Not configured</dd></div><div><dt>OAuth authorization</dt><dd>Not available</dd></div></dl><p>MCP is the planned protocol for sharing approved context with compatible assistants. Compatibility with individual assistants has not been verified. Local developer testing does not provide a customer connection.</p></details>}
            </>}
          </section>
          <p className="admoji-connections-footer">Nothing to configure yet. Setup options will appear here as connections become available.</p>
        </div>
      </div>
    </div>
  );
}
